import { useCallback, useEffect, useRef, useState } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Un solo pedido compartido entre las cuentas, los libros y las acciones locales.
// El temporizador de Bancos sigue siendo el único polling al proveedor.
export const useOtpBankStatus = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cycle, setCycle] = useState(0);
  const active = useRef(true);
  const flight = useRef(null);
  const controller = useRef(null);
  const queued = useRef(false);
  const lastAttempt = useRef(0);
  const refreshRef = useRef(null);

  const refresh = useCallback(({ afterChange = false } = {}) => {
    if (!active.current) return Promise.resolve();
    if (flight.current) {
      if (afterChange) queued.current = true;
      return flight.current;
    }
    if (!afterChange && Date.now() - lastAttempt.current < 2000) return Promise.resolve();
    lastAttempt.current = Date.now();
    const abort = new AbortController();
    controller.current = abort;
    const timeout = setTimeout(() => abort.abort(), 45000);
    setLoading(true);
    const request = (async () => {
      try {
        const response = await fetch(`${API}/bancos/1touchpay/status`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          signal: abort.signal,
        });
        if (!response.ok) throw new Error('No se pudo actualizar el saldo bancario');
        const result = await response.json();
        if (active.current && controller.current === abort && !abort.signal.aborted) {
          setData({ ...result, checked_at: new Date().toISOString() });
          setError(result.reason || (!result.active ? 'Sin cuentas bancarias disponibles' : ''));
        }
      } catch (e) {
        if (active.current && controller.current === abort) setError(e.name === 'AbortError' ? 'La consulta bancaria agotó el tiempo de espera' : e.message);
      } finally {
        clearTimeout(timeout);
        if (controller.current === abort) flight.current = null;
        if (active.current && controller.current === abort) {
          setLoading(false);
          setCycle(value => value + 1);
          if (queued.current) {
            queued.current = false;
            refreshRef.current({ afterChange: true });
          }
        }
      }
    })();
    flight.current = request;
    return request;
  }, []);
  refreshRef.current = refresh;

  useEffect(() => {
    active.current = true;
    lastAttempt.current = 0;
    refresh();
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      active.current = false;
      queued.current = false;
      controller.current?.abort();
      controller.current = null;
      flight.current = null;
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refresh]);

  const refreshBooks = useCallback(() => { if (active.current) setCycle(value => value + 1); }, []);
  return { data, loading, error, cycle, refresh, refreshBooks };
};